// SPDX-License-Identifier: AGPL-3.0-or-later

// LOCAL-ONLY: channel options of the composer's autocomplete (nested-ordering-plan.md B15), moved out of
// useLexicalAutocomplete so the order can be tested: the # mention list breaks equal match ranks in sidebar (tree)
// order instead of by name, and a command's channel slot lists its matches in sidebar order instead of raw stored
// position. Exclude from upstream sync.

import type {AutocompleteOption} from '@app/features/channel/components/AutocompleteTypes';
import type {Channel} from '@app/features/channel/models/Channel';
import Channels from '@app/features/channel/state/Channels';
import {guildChannelTree, sortByChannelTree} from '@app/features/channel/utils/ChannelTreeLabels';
import {MENTION_RESULT_LIMIT} from '@app/features/messaging/utils/AutocompleteOptionBuilders';
import {matchSorter} from 'match-sorter';

/** The # mention matches: the match rank first, then the sidebar order (unknown channels last, in input order). */
export function matchChannelsInTreeOrder(
	guildId: string,
	candidates: ReadonlyArray<Channel>,
	query: string,
): Array<Channel> {
	const tree = guildChannelTree(guildId);
	const at = (candidate: Channel) => tree.byId.get(candidate.id)?.index ?? Number.MAX_SAFE_INTEGER;
	return matchSorter([...candidates], query, {
		keys: ['name'],
		baseSort: (a, b) => at(a.item) - at(b.item) || a.index - b.index,
	});
}

export function buildSlotChannelOptions(channel: Channel | null, query: string): Array<AutocompleteOption> {
	if (channel == null || channel.guildId == null) {
		return [];
	}
	const channels = Channels.getGuildChannels(channel.guildId).filter((candidate) => !candidate.isGuildCategory());
	const matched = query.length === 0 ? channels : matchSorter(channels, query, {keys: ['name']});
	return sortByChannelTree(guildChannelTree(channel.guildId), matched, (candidate) => candidate.id)
		.slice(0, MENTION_RESULT_LIMIT)
		.map((option) => ({
			type: 'channel' as const,
			channel: option,
		}));
}
